"use client"

import { useState, useRef, useEffect } from "react"
import { useMutation, useQuery, useQueries } from "@tanstack/react-query"
import { ChevronDown, ChevronUp, ExternalLink, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { WatchlistStockTable, type ColKey } from "@/components/backtest/WatchlistStockTable"
import { AddToAutoTradingButton } from "@/components/watchlist/AddToAutoTradingButton"
import { StockVerificationTab } from "@/components/backtest/StockVerificationTab"
import { DailyRiseHistoryTab } from "@/components/backtest/DailyRiseHistoryTab"
import { BacktestResultTab } from "@/components/backtest/BacktestResultTab"
import { DisclosuresTab } from "@/components/backtest/DisclosuresTab"
import { getBacktestJob, runAllBacktests } from "@/lib/api/backtest"
import { getWatchlist, getWatchlistFilterOptions } from "@/lib/api/watchlist"
import { getCurrentPrices } from "@/lib/api/prices"
import { getFinancialYears, getFinancials } from "@/lib/api/financials"
import { Skeleton } from "@/components/ui/skeleton"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import type { BacktestWatchlistItem, WatchlistFilterOptions } from "@/types/backtest"
import type { FinancialStatement } from "@/types/financial"
import type { CurrentPrice } from "@/types/price"

const WATCHLIST_PAGE_SIZE = 50

const EMPTY_FILTER_OPTIONS: WatchlistFilterOptions = {
  conditionNames: [],
  marketTypes: [],
  sectors: [],
  states: [],
}

// 1 2 3 ... n 형태의 페이지 번호 목록 (현재 페이지 좌우 1칸 + 처음/끝 고정)
function getPageList(current: number, total: number): (number | "ellipsis")[] {
  const pages: (number | "ellipsis")[] = []
  for (let p = 1; p <= total; p++) {
    if (p === 1 || p === total || Math.abs(p - current) <= 1) {
      pages.push(p)
    } else if (pages[pages.length - 1] !== "ellipsis") {
      pages.push("ellipsis")
    }
  }
  return pages
}

function WatchlistPagination({
  page,
  totalPages,
  onChange,
}: {
  page: number
  totalPages: number
  onChange: (p: number) => void
}) {
  const pages = getPageList(page, totalPages)
  const linkClass = "border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            text="이전"
            onClick={(e) => {
              e.preventDefault()
              if (page > 1) onChange(page - 1)
            }}
            className={cn(linkClass, page === 1 && "pointer-events-none opacity-40")}
          />
        </PaginationItem>

        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <PaginationItem key={`e${i}`}>
              <PaginationEllipsis className="text-zinc-500" />
            </PaginationItem>
          ) : (
            <PaginationItem key={p}>
              <PaginationLink
                href="#"
                isActive={p === page}
                onClick={(e) => {
                  e.preventDefault()
                  onChange(p)
                }}
                className={
                  p === page
                    ? "border-emerald-500 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/10"
                    : linkClass
                }
              >
                {p}
              </PaginationLink>
            </PaginationItem>
          )
        )}

        <PaginationItem>
          <PaginationNext
            href="#"
            text="다음"
            onClick={(e) => {
              e.preventDefault()
              if (page < totalPages) onChange(page + 1)
            }}
            className={cn(linkClass, page === totalPages && "pointer-events-none opacity-40")}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

// 백테스트 실행 대상 조건식 (BacktestService.AllConditionNames와 동일)
const RUNNABLE_CONDITIONS = ["유동주식수", "일봉주봉", "믹스매매"]

function ConditionMultiSelect({
  options,
  selected,
  onChange,
}: {
  options: string[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
}) {
  const [open, setOpen] = useState(false)

  const toggle = (v: string) => {
    const next = new Set(selected)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    onChange(next)
  }

  const label =
    selected.size === 0
      ? "조건식 선택"
      : selected.size === options.length
        ? "전체 조건식"
        : [...selected].join(", ")

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-zinc-800"
        >
          <span className="max-w-40 truncate">{label}</span>
          <ChevronDown className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-2" align="start">
        <div className="space-y-0.5">
          {options.map((v) => (
            <label key={v} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-zinc-800">
              <Checkbox checked={selected.has(v)} onCheckedChange={() => toggle(v)} />
              <span className="text-xs text-zinc-300">{v}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// 진행 중인 전체 조건식 백테스트의 jobId를 새로고침 후에도 이어서 폴링하기 위한 저장 키
const BACKTEST_JOB_ID_KEY = "backtest:activeJobId"

// 백테스트 watchlist 현재가 폴링 허용 시간대: 09:00~10:30, 14:30~15:30
// (포트폴리오/자동매매 30초 폴링에는 적용하지 않음)
function isBacktestPollingTime(): boolean {
  const now = new Date()
  const totalMinutes = now.getHours() * 60 + now.getMinutes()

  const slot1Start = 9 * 60        // 09:00
  const slot1End   = 10 * 60 + 30  // 10:30
  const slot2Start = 14 * 60 + 30  // 14:30
  const slot2End   = 15 * 60 + 30  // 15:30

  return (totalMinutes >= slot1Start && totalMinutes <= slot1End) ||
         (totalMinutes >= slot2Start && totalMinutes <= slot2End)
}

// ── 재무제표 요약 ────────────────────────────────────────────────────────────────

function fmtAmt(n: number | null | undefined): string {
  if (n == null) return "-"
  return n.toLocaleString("ko-KR")
}

function amtColor(n: number | null | undefined) {
  if (n == null) return "text-zinc-400"
  return n < 0 ? "text-blue-400" : n > 0 ? "text-red-400" : "text-zinc-400"
}

function DeltaIcon({ curr, prev }: { curr: number | null; prev: number | null }) {
  if (curr == null || prev == null) return <Minus className="size-3 text-zinc-600" />
  if (curr > prev) return <TrendingUp   className="size-3 text-red-400"  />
  if (curr < prev) return <TrendingDown className="size-3 text-blue-400" />
  return <Minus className="size-3 text-zinc-600" />
}

function findAmt(rows: FinancialStatement[], match: string): number | null {
  return rows.find((r) => r.accountNm.includes(match))?.amount ?? null
}

function calcDebtRatio(rows: FinancialStatement[]): number | null {
  const liabilities = rows.find((r) => r.accountNm === "부채총계")?.amount
  const equity      = rows.find((r) => r.accountNm === "자본총계")?.amount
  if (liabilities == null || equity == null || equity === 0) return null
  return Math.round((liabilities / equity) * 100)
}

const SUMMARY_METRICS: { label: string; getValue: (rows: FinancialStatement[]) => number | null; isRatio?: boolean }[] = [
  { label: "매출액",     getValue: (r) => findAmt(r, "매출액")     },
  { label: "영업이익",   getValue: (r) => findAmt(r, "영업이익")    },
  { label: "당기순이익", getValue: (r) => findAmt(r, "당기순이익")  },
  { label: "부채비율",   getValue: calcDebtRatio, isRatio: true     },
]

function FinancialSummaryTab({ code }: { code: string }) {
  const { data: years = [], isLoading: yearsLoading } = useQuery<string[]>({
    queryKey: ["financials", code, "years"],
    queryFn:  () => getFinancialYears(code),
    enabled:  !!code,
  })

  const displayYears = years.slice(0, 4)

  const results = useQueries({
    queries: displayYears.map((y) => ({
      queryKey: ["financials", code, y, "11011", "all"],
      queryFn:  () => getFinancials(code, y, "11011"),
      enabled:  !!code && displayYears.length > 0,
    })),
  })

  const isLoading = yearsLoading || results.some((r) => r.isLoading)

  const yearData = displayYears.map((y, i) => ({
    year: y,
    rows: (results[i]?.data ?? []) as FinancialStatement[],
  }))

  if (isLoading) {
    return (
      <div className="overflow-x-auto p-3">
        <div className="flex gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-28 bg-zinc-800" />
          ))}
        </div>
      </div>
    )
  }

  if (displayYears.length === 0) {
    return (
      <div className="flex h-20 items-center justify-center text-xs text-zinc-500">
        재무 데이터가 없습니다.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-zinc-800">
            <th className="px-3 py-2 text-left font-medium text-zinc-500 w-20">지표</th>
            {yearData.map(({ year }) => (
              <th key={year} className="px-3 py-2 text-right font-medium text-zinc-400">{year}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SUMMARY_METRICS.map(({ label, getValue, isRatio }) => (
            <tr key={label} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
              <td className="px-3 py-1.5 text-zinc-400">{label}</td>
              {yearData.map(({ year }, i) => {
                const curr = getValue(yearData[i].rows)
                const prev = i < yearData.length - 1 ? getValue(yearData[i + 1].rows) : null
                return (
                  <td key={year} className="px-3 py-1.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <DeltaIcon curr={curr} prev={prev} />
                      <span className={cn("font-mono", isRatio ? "text-zinc-300" : amtColor(curr))}>
                        {isRatio ? (curr != null ? `${curr}%` : "-") : fmtAmt(curr)}
                      </span>
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

const DETAIL_TABS = [
  { key: "financial",       label: "재무제표" },
  { key: "history",         label: "요약히스토리" },
  { key: "verify",          label: "종목검증" },
  { key: "backtestResult",  label: "백테스트 결과" },
  { key: "disclosures",     label: "공시" },
] as const

type DetailTabKey = (typeof DETAIL_TABS)[number]["key"]

export default function BacktestPage() {
  const [selected,   setSelected]   = useState<BacktestWatchlistItem | null>(null)
  const [detailTab,  setDetailTab]  = useState<DetailTabKey>("financial")
  const [mobileOpen, setMobileOpen] = useState(false)

  const [topPct,  setTopPct]  = useState(60)
  const splitRef = useRef<HTMLDivElement>(null)
  const dragRef  = useRef(false)

  // main의 overflow-auto를 hidden으로 덮어써서 백테스트 페이지가 뷰포트를 꽉 채우게 함
  useEffect(() => {
    const main = document.querySelector<HTMLElement>("main")
    if (!main) return
    main.style.overflow = "hidden"
    return () => { main.style.overflow = "" }
  }, [])

  useEffect(() => {
    const move = (clientY: number) => {
      if (!dragRef.current || !splitRef.current) return
      const rect = splitRef.current.getBoundingClientRect()
      const pct  = ((clientY - rect.top) / rect.height) * 100
      setTopPct(Math.min(Math.max(pct, 20), 80))
    }
    const end = () => {
      if (!dragRef.current) return
      dragRef.current = false
      document.body.style.userSelect = ""
      document.body.style.cursor     = ""
    }
    const onMouseMove  = (e: MouseEvent) => move(e.clientY)
    const onTouchMove  = (e: TouchEvent) => { e.preventDefault(); move(e.touches[0].clientY) }
    document.addEventListener("mousemove",  onMouseMove)
    document.addEventListener("mouseup",    end)
    document.addEventListener("touchmove",  onTouchMove, { passive: false })
    document.addEventListener("touchend",   end)
    return () => {
      document.removeEventListener("mousemove",  onMouseMove)
      document.removeEventListener("mouseup",    end)
      document.removeEventListener("touchmove",  onTouchMove)
      document.removeEventListener("touchend",   end)
    }
  }, [])

  const onDragStart = () => {
    dragRef.current = true
    document.body.style.userSelect = "none"
    document.body.style.cursor     = "row-resize"
  }

  const today = new Date().toISOString().slice(0, 10)

  // 시장/섹터/조건식/상태 필터 — 백엔드가 페이지네이션과 함께 필터링하므로
  // 옵션 목록은 별도 엔드포인트에서, 선택 상태는 여기서 관리한다.
  const [page, setPage] = useState(1)
  const [colFilters, setColFilters] = useState<Record<ColKey, Set<string>>>({
    marketType:    new Set(),
    sector:        new Set(),
    conditionName: new Set(),
    state:         new Set(),
  })

  const handleFilterChange = (key: ColKey, val: Set<string>) => {
    setColFilters((prev) => ({ ...prev, [key]: val }))
    setPage(1) // 필터 변경 시 1페이지로 리셋
  }

  const { data: filterOptions = EMPTY_FILTER_OPTIONS } = useQuery({
    queryKey: ["backtest", "watchlist", "filters"],
    queryFn: getWatchlistFilterOptions,
    staleTime: 5 * 60_000,
  })

  const { data: watchlistPage, isLoading } = useQuery({
    queryKey: [
      "backtest", "watchlist", page,
      [...colFilters.marketType], [...colFilters.sector],
      [...colFilters.conditionName], [...colFilters.state],
    ],
    queryFn: () => getWatchlist({
      page,
      pageSize:      WATCHLIST_PAGE_SIZE,
      marketType:    colFilters.marketType.size    > 0 ? [...colFilters.marketType].join(",")    : undefined,
      sector:        colFilters.sector.size        > 0 ? [...colFilters.sector].join(",")        : undefined,
      conditionName: colFilters.conditionName.size > 0 ? [...colFilters.conditionName].join(",") : undefined,
      state:         colFilters.state.size         > 0 ? [...colFilters.state].join(",")         : undefined,
    }),
    placeholderData: (prev) => prev,
  })

  const items = watchlistPage?.items ?? []
  const totalPages = watchlistPage?.totalPages ?? 1

  const todayCodes = [...new Set(
    items.filter((i) => i.searchedDate === today).map((i) => i.stockCode)
  )]

  const lastPricesRef = useRef<CurrentPrice[]>([])

  const { data: currentPricesList = [] } = useQuery({
    queryKey: ["prices", "current", "backtest", todayCodes],
    queryFn: async () => {
      // 허용 시간대(09:00~10:30, 14:30~15:30) 밖에서는 API 호출 없이 스킵
      if (!isBacktestPollingTime()) return lastPricesRef.current
      const result = await getCurrentPrices(todayCodes)
      lastPricesRef.current = result
      return result
    },
    enabled: todayCodes.length > 0,
    refetchInterval: 300_000,
    staleTime: 290_000,
  })

  const currentPricesMap = Object.fromEntries(currentPricesList.map((p) => [p.stockCode, p]))

  const handleSelect = (item: BacktestWatchlistItem) => {
    setSelected(item)
    setMobileOpen(true)
  }

  // ── 조건식(유동주식수/일봉주봉/믹스매매) 백테스트 실행 + 진행상황 폴링 ──
  const [activeJobId, setActiveJobId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null
    const stored = localStorage.getItem(BACKTEST_JOB_ID_KEY)
    return stored ? Number(stored) : null
  })
  const [selectedConditions, setSelectedConditions] = useState<Set<string>>(new Set(RUNNABLE_CONDITIONS))

  const runAllMutation = useMutation({
    mutationFn: () => runAllBacktests([...selectedConditions]),
    onSuccess: ({ jobId }) => {
      setActiveJobId(jobId)
      localStorage.setItem(BACKTEST_JOB_ID_KEY, String(jobId))
      setDetailTab("backtestResult")
    },
  })

  const { data: activeJob } = useQuery({
    queryKey: ["backtest", "job", activeJobId],
    queryFn: () => getBacktestJob(activeJobId!),
    enabled: activeJobId != null,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 2000 : false),
  })

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden">

      {/* ── 상단: 백테스트 실행 ── */}
      <div className="flex shrink-0 items-center gap-3">
        <ConditionMultiSelect
          options={RUNNABLE_CONDITIONS}
          selected={selectedConditions}
          onChange={setSelectedConditions}
        />
        <button
          onClick={() => runAllMutation.mutate()}
          disabled={selectedConditions.size === 0 || runAllMutation.isPending || activeJob?.status === "running"}
          className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {activeJob?.status === "running" && <Loader2 className="size-3.5 animate-spin" />}
          백테스트 실행
        </button>
        {activeJob?.status === "running" && (
          <span className="text-xs text-zinc-400">
            {activeJob.processedCount}/{activeJob.totalCount} 조건식 진행 중
          </span>
        )}
        {runAllMutation.isError && (
          <span className="text-xs text-red-400">실행 시작에 실패했습니다.</span>
        )}
      </div>

      {/* ── 분할 영역: 상단 테이블 + 드래그 핸들 + 하단 패널 ── */}
      <div ref={splitRef} className="flex min-h-0 min-w-0 flex-1 flex-col">

        {/* 상단: 종목 테이블 — 뷰포트 기준으로 고정된 부모 높이(topPct%)를 그대로 유지하되,
            테이블 영역만 스크롤되고 페이지네이션 바는 하단에 고정, 하단 패널 위치는
            밀리지 않도록 함.
            세로/가로 스크롤 모두 WatchlistStockTable 내부(shadcn Table)의
            overflow-x-auto 컨테이너가 전담한다 (WatchlistStockTable.tsx에서
            높이를 100%로 채우고 overflow-y-auto를 추가) — 이 래퍼는 높이만
            고정해서 넘겨주는 역할만 한다 */}
        <div
          className="flex min-w-0 shrink-0 flex-col rounded-lg border border-zinc-800 bg-zinc-900"
          style={{ height: `${topPct}%` }}
        >
          <div className="min-h-0 flex-1 overflow-hidden">
            <WatchlistStockTable
              items={items}
              isLoading={isLoading}
              selectedCode={selected?.stockCode}
              onSelect={handleSelect}
              currentPricesMap={currentPricesMap}
              today={today}
              renderAutoTradingColumn={(item) => (
                <AddToAutoTradingButton stockCode={item.stockCode} added={item.hasAutoTradingSettings} />
              )}
              filterOptions={filterOptions}
              colFilters={colFilters}
              onFilterChange={handleFilterChange}
            />
          </div>

          {watchlistPage && watchlistPage.totalCount > 0 && (
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-zinc-800 px-3 py-2">
              <span className="text-xs text-zinc-500">
                총 {watchlistPage.totalCount.toLocaleString("ko-KR")}건
              </span>
              {totalPages > 1 && (
                <WatchlistPagination page={page} totalPages={totalPages} onChange={setPage} />
              )}
            </div>
          )}
        </div>

        {/* 드래그 핸들 */}
        <div
          className="h-1 shrink-0 cursor-row-resize bg-zinc-800 transition-colors hover:bg-emerald-500/40"
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
        />

        {/* 하단: 종목 상세 패널 */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900">

          {/* 패널 헤더 */}
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-zinc-800 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-3">
              {selected ? (
                <>
                  <span className="truncate font-semibold text-zinc-100">{selected.stockName}</span>
                  <span className="shrink-0 font-mono text-xs text-zinc-500">{selected.stockCode}</span>
                  <span className="shrink-0 text-xs text-zinc-500">{selected.searchedDate}</span>
                  <Link
                    href={`/chart/${selected.stockCode}?date=${selected.searchedDate}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex shrink-0 items-center gap-1 text-xs text-emerald-400 hover:underline"
                  >
                    차트 <ExternalLink className="size-3" />
                  </Link>
                </>
              ) : (
                <span className="text-sm text-zinc-500">종목을 선택하세요</span>
              )}
            </div>

            {/* 모바일: 접기/펼치기 버튼 */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="flex shrink-0 items-center gap-1 text-xs text-zinc-500 md:hidden"
            >
              {mobileOpen ? (
                <><ChevronUp className="size-4" />접기</>
              ) : (
                <><ChevronDown className="size-4" />펼치기</>
              )}
            </button>
          </div>

          {/* 서브탭 (md+: 항상 표시 / 모바일: 토글) */}
          <div className={cn("flex min-h-0 flex-1 flex-col md:flex", mobileOpen ? "flex" : "hidden")}>

            {/* 탭 헤더 */}
            <div className="flex shrink-0 border-b border-zinc-800 px-4">
              {DETAIL_TABS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setDetailTab(key)}
                  className={cn(
                    "min-h-[36px] px-4 text-xs font-medium transition-colors",
                    "border-b-2 -mb-px",
                    detailTab === key
                      ? "border-emerald-400 text-emerald-400"
                      : "border-transparent text-zinc-400 hover:text-zinc-200"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* 탭 콘텐츠 */}
            <div className="flex-1 overflow-auto">
              {detailTab === "financial" ? (
                selected ? (
                  <FinancialSummaryTab code={selected.stockCode} />
                ) : (
                  <div className="flex h-20 items-center justify-center text-xs text-zinc-500">
                    종목을 선택하세요.
                  </div>
                )
              ) : detailTab === "history" ? (
                <DailyRiseHistoryTab selected={selected} />
              ) : detailTab === "verify" ? (
                <StockVerificationTab selected={selected} />
              ) : detailTab === "backtestResult" ? (
                <BacktestResultTab job={activeJob ?? null} />
              ) : (
                <div className="flex h-20 items-center justify-center text-xs text-zinc-500">
                  준비 중
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}
